import '../ManageLessons.scss';
import { ReactComponent as InfoIcon } from '../../../../../../images/icons/Info.svg';
import { EditFileButton } from 'components/Admin/EditFileButton/EditFileButton';
import { LanguageType } from 'types/languages';
import { getLanguage, getLanguageShortText } from 'utilities/languages';
import { useCallback, useState } from 'react';
import { useServices } from 'index';
import { TextLessonField } from './TextLessonField';
import { AdminMedia } from 'types/admin/media';
import { FileManagerModal } from 'components/Admin/Modals/FileManagerModal/FileManagerModal';
import { FileNode } from 'types/admin/nodes';
import { useQuery } from '@tanstack/react-query';

type Props = {
  images: AdminMedia[];
  description: string;
  handleImages: (lessonContent: AdminMedia[]) => void;
  handleDescription: (description: string) => void;
};

export const PictureLessonField = ({ images, description, handleImages, handleDescription }: Props) => {
  const languages: LanguageType[] = ['de-DE', 'en-US', 'es-ES', 'ja-JP', 'ko-KR', 'zh-CN'];

  const handleImagesSelection = useCallback((media: FileNode, languageType: LanguageType) => {
    if (!images) {
      handleImages([{ countryCode: languageType, mediaId: media.id }]);
    } else {
      const hasMediaForLanguage = images.find((el) => el.countryCode === languageType);
      if (hasMediaForLanguage) {
        handleImages(
          images.map((image) => (image.countryCode !== languageType ? image : { countryCode: languageType, mediaId: media.id }))
        );
      } else {
        handleImages([...images, { countryCode: languageType, mediaId: media.id }]);
      }
    }
  }, [images, handleImages]);

  return (
    <div className='picture-lesson-wrapper'>
      <div>
        <InfoIcon />
        <span>When an image is missing for a language, the English (en) image will be used by default.</span>
      </div>
      <div>
        {languages.map((language, index) => (
          <PicturesListEntry
            key={index}
            languageType={language}
            imageId={images?.find((c) => c.countryCode === language)?.mediaId ?? ''}
            handleImageSelection={handleImagesSelection}
          />
        ))}
      </div>
      <TextLessonField lessonContent={description} handleLessonContent={handleDescription} />
    </div>
  );
};

type PicturesListEntryProps = {
  languageType: LanguageType;
  imageId: string;
  handleImageSelection: (media: FileNode, languageType: LanguageType) => void;
};

const PicturesListEntry = ({ languageType, imageId, handleImageSelection }: PicturesListEntryProps) => {
  const service = useServices().mediaService;

  const { data } = useQuery({
    queryKey: ['lessonImage', `${imageId}-${languageType}`],
    queryFn: () => service.getMedia(imageId),
    enabled: !!imageId,
  });
  const invalidPictureIdFromDB = '00000000-0000-0000-0000-000000000000'; // fix for the TM learning materials added in the past
  const [isFileManagerModalOn, setIsFileManagerModalOn] = useState<boolean>(false);

  const openFileManagerModal = useCallback(() => {
    setIsFileManagerModalOn(true);
  }, []);

  const onClose = useCallback((media: FileNode | undefined) => {
    if (media) {
      handleImageSelection(media, languageType);
    }
    setIsFileManagerModalOn(false);
  }, [handleImageSelection, languageType]);

  return (
    <div className='pictures-list-entry'>
      {imageId && imageId !== invalidPictureIdFromDB ? (
        <img className='lesson-image' src={data?.fileUrl ? data.fileUrl : ''} alt={data?.originalFilename ? data.originalFilename : ''} />
      ) : (
        <div className='lesson-image lesson-image__placeholder'></div>
      )}
      <span>
        {getLanguage(languageType)} ({getLanguageShortText(languageType)})
      </span>
      <EditFileButton isAdd={!imageId} onClick={openFileManagerModal} />
      {isFileManagerModalOn ? <FileManagerModal open={isFileManagerModalOn} onClose={onClose} /> : <></>}
    </div>
  );
};
